import { useEffect, useState } from "react";

// Acompanha o teclado virtual do celular.
//
// O PORQUÊ: no navegador de celular, abrir o teclado NÃO encolhe a altura do
// layout — `100dvh`/`h-dvh` continua valendo a tela inteira. Quem encolhe é o
// `visualViewport`, que é a faixa realmente visível acima do teclado. O
// resultado, sem tratamento, é o rodapé da conversa (a caixa de digitar)
// ficando atrás do teclado e o cabeçalho subindo para fora da tela quando o
// navegador empurra a página para revelar o campo focado.
//
// Este hook lê `window.visualViewport` e devolve o que a tela precisa para se
// encaixar na faixa visível:
//   - alturaVisivel: altura da faixa acima do teclado (px)
//   - deslocamento:  quanto o navegador empurrou a página para cima
//                    (`offsetTop`) — compensar isso traz o cabeçalho de volta
//   - tecladoAberto: só então vale mexer no layout; fora disso o CSS normal
//                    (h-dvh) já resolve e mexer atrapalharia o desktop.
//
// Limite de 150px: a barra de endereço do navegador aparecendo/sumindo tira
// uns 60–100px do visualViewport, e isso não é teclado. Teclado de celular
// passa dos 200px com folga.
const LIMITE_TECLADO = 150;

export default function useTecladoVirtual() {
  const [estado, setEstado] = useState({
    alturaVisivel: 0,
    deslocamento: 0,
    tecladoAberto: false,
  });

  useEffect(() => {
    const vv = window.visualViewport;
    // Sem visualViewport (desktop antigo) o comportamento padrão já serve.
    if (!vv) return undefined;

    let quadro = null;

    const medir = () => {
      quadro = null;
      const alturaVisivel = Math.round(vv.height);
      const deslocamento = Math.round(vv.offsetTop);
      // O que sobrou do layout viewport e não está visível é, na prática, o
      // teclado ocupando a parte de baixo.
      const coberto = window.innerHeight - alturaVisivel - deslocamento;
      const tecladoAberto = coberto > LIMITE_TECLADO;

      setEstado((anterior) => {
        if (
          anterior.alturaVisivel === alturaVisivel &&
          anterior.deslocamento === deslocamento &&
          anterior.tecladoAberto === tecladoAberto
        ) {
          // Sem isto, cada `scroll` do visualViewport (que dispara em rajada
          // ao rolar a conversa) renderizaria a tela de novo à toa.
          return anterior;
        }
        return { alturaVisivel, deslocamento, tecladoAberto };
      });
    };

    // O teclado abre com animação: o evento dispara várias vezes durante ela.
    // Agrupar num quadro evita recalcular a cada milímetro.
    const agendar = () => {
      if (quadro === null) quadro = requestAnimationFrame(medir);
    };

    medir();
    vv.addEventListener("resize", agendar);
    vv.addEventListener("scroll", agendar);

    return () => {
      if (quadro !== null) cancelAnimationFrame(quadro);
      vv.removeEventListener("resize", agendar);
      vv.removeEventListener("scroll", agendar);
    };
  }, []);

  return estado;
}
